import { useEffect, useState, useCallback, useRef } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
    Gamepad2, Users, Trophy, X, Swords, ArrowLeft, AlertCircle, Loader2, Clock, Lock
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../hooks/useAuth";
import {
    fetchOnlinePlayers, fetchMyGameStats, fetchLeaderboard,
    submitSinglePlayerScore, GAME_CATALOG, getGameDef
} from "../services/crmServices/gameService";
import { connectSocket, emitSocket, onSocket } from "../services/socketService";

// Game components
import { Game2048 } from "../components/games/Game2048";
import { GameReaction } from "../components/games/GameReaction";
import { GameMemory } from "../components/games/GameMemory";
import { GameSnake } from "../components/games/GameSnake";
import { GameTicTacToe } from "../components/games/GameTicTacToe";
import { GameRPS } from "../components/games/GameRPS";
import { GameQuiz } from "../components/games/GameQuiz";
import { GameTyping } from "../components/games/GameTyping";

import "./Games.css";

const SINGLE_GAMES = GAME_CATALOG.filter((g) => g.mode === "single");
const MULTI_GAMES = GAME_CATALOG.filter((g) => g.mode === "multi");

export function Games() {
    const { user } = useAuth();

    // Permission check
    const hasAccess = !!user?.permissions?.games?.access;
    const canMultiplayer = !!user?.permissions?.games?.multiplayer !== false && hasAccess;

    const [online, setOnline] = useState([]);
    const [stats, setStats] = useState(null);
    const [leaderboard, setLeaderboard] = useState([]);
    const [leaderboardGame, setLeaderboardGame] = useState(""); // "" = all games
    const [leaderboardPeriod, setLeaderboardPeriod] = useState("alltime");

    // Active game (single or multi)
    const [activeGameId, setActiveGameId] = useState(null);    // single-player active
    const [activeMatch, setActiveMatch] = useState(null);      // multiplayer match
    const [pendingChallengeTo, setPendingChallengeTo] = useState(null); // userId we challenged
    const [showChallengeFor, setShowChallengeFor] = useState(null);     // userId to pick game for
    const [dailyLimitReached, setDailyLimitReached] = useState(false);

    // ── Load initial data + socket setup ─────────────────────────────────
    const refreshAll = useCallback(async () => {
        if (!hasAccess) return;
        try {
            const [onlineRes, statsRes, lbRes] = await Promise.all([
                fetchOnlinePlayers(),
                fetchMyGameStats(),
                fetchLeaderboard({ period: leaderboardPeriod, limit: 10 })
            ]);
            setOnline(onlineRes?.data || []);
            setStats(statsRes?.data || null);
            setLeaderboard(lbRes?.data || []);
            const data = statsRes?.data;
            if (data?.dailyLimitMinutes > 0 && data?.playMsToday >= data.dailyLimitMinutes * 60 * 1000) {
                setDailyLimitReached(true);
            }
        } catch (err) { /* ignore */ }
    }, [hasAccess, leaderboardPeriod]);

    useEffect(() => { refreshAll(); }, [refreshAll]);

    // Backup: poll online list every 15s in case socket events get missed
    useEffect(() => {
        if (!hasAccess) return;
        const id = setInterval(async () => {
            try {
                const res = await fetchOnlinePlayers();
                setOnline(res?.data || []);
            } catch { /* ignore */ }
        }, 15 * 1000);
        return () => clearInterval(id);
    }, [hasAccess]);

    // Re-fetch leaderboard when filters change
    useEffect(() => {
        if (!hasAccess) return;
        (async () => {
            try {
                const res = await fetchLeaderboard({ gameId: leaderboardGame || undefined, period: leaderboardPeriod, limit: 10 });
                setLeaderboard(res?.data || []);
            } catch { /* ignore */ }
        })();
    }, [leaderboardGame, leaderboardPeriod, hasAccess]);

    // ── Socket lobby presence ────────────────────────────────────────────
    useEffect(() => {
        if (!hasAccess) return;
        const socket = connectSocket();
        if (!socket) {
            console.warn("[games] No socket — auth token missing?");
            return;
        }

        const announce = () => {
            const ok = emitSocket("game:join", {});
            console.log(`[games] game:join sent (socket.connected=${socket.connected}, emit ok=${ok})`);
        };
        // Try immediately if already connected
        if (socket.connected) announce();
        // Also fire on every (re)connect
        socket.on("connect", announce);
        // Defensive: re-announce every 20s in case lobby state was lost server-side
        const rejoinTimer = setInterval(() => {
            if (socket.connected) announce();
        }, 20 * 1000);

        const offOnline = onSocket("game:online_update", ({ players }) => {
            console.log(`[games] online_update received: ${players?.length || 0} players`);
            setOnline(players || []);
        });
        const offStarted = onSocket("game:match_started", ({ match }) => {
            setActiveMatch(match);
            setActiveGameId(null);
            setPendingChallengeTo(null);
            setShowChallengeFor(null);
        });
        const offState = onSocket("game:match_state", ({ matchId, state }) => {
            setActiveMatch((m) => (m && m.id === matchId ? { ...m, state } : m));
        });
        const offEnd = onSocket("game:match_ended", ({ matchId, state, result }) => {
            setActiveMatch((m) => (m && m.id === matchId ? { ...m, state, finished: true, result } : m));
            // Refresh stats / leaderboard so the result lands fast
            setTimeout(refreshAll, 800);
        });
        const offDecline = onSocket("game:challenge_declined", () => {
            toast.info("Challenge declined");
            setPendingChallengeTo(null);
        });
        const offError = onSocket("game:error", ({ message }) => {
            toast.error(message);
            setPendingChallengeTo(null);
        });

        return () => {
            emitSocket("game:leave", {});
            socket.off("connect", announce);
            clearInterval(rejoinTimer);
            offOnline?.(); offStarted?.(); offState?.(); offEnd?.(); offDecline?.(); offError?.();
        };
    }, [hasAccess, refreshAll]);

    // ── Single-player score submission ───────────────────────────────────
    const handleSinglePlayerScore = useCallback(async ({ score, durationMs }) => {
        try {
            await submitSinglePlayerScore({ gameId: activeGameId, score, durationMs });
            refreshAll();
        } catch (err) {
            if (err?.response?.status === 429) {
                setDailyLimitReached(true);
            }
        }
    }, [activeGameId, refreshAll]);

    // ── Challenge / multiplayer handlers ─────────────────────────────────
    const challenge = (toUserId, gameId) => {
        setShowChallengeFor(null);
        setPendingChallengeTo(toUserId);
        emitSocket("game:challenge", { toUserId, gameId });
        toast("Challenge sent — waiting for response", { duration: 4000 });
    };

    const leaveMatch = () => {
        if (activeMatch && !activeMatch.finished) {
            emitSocket("game:leave_match", { matchId: activeMatch.id });
        }
        setActiveMatch(null);
    };

    // ── Guard screens ────────────────────────────────────────────────────
    if (!hasAccess) {
        return (
            <div className="container mx-auto px-4 py-12">
                <div className="max-w-md mx-auto bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center">
                    <div className="p-3 bg-amber-50 rounded-full w-fit mx-auto mb-4">
                        <Lock className="h-8 w-8 text-amber-500" />
                    </div>
                    <h2 className="text-lg font-semibold text-gray-900 mb-2">Games access not enabled</h2>
                    <p className="text-sm text-gray-500">Ask your admin to enable Games for your account.</p>
                </div>
            </div>
        );
    }

    // ── Active multiplayer match ─────────────────────────────────────────
    if (activeMatch) {
        return (
            <div className="container mx-auto px-4 py-6">
                <div className="flex items-center justify-between mb-4">
                    <Button size="sm" variant="outline" onClick={leaveMatch}>
                        <ArrowLeft className="h-3.5 w-3.5 mr-1.5" />
                        Leave match
                    </Button>
                    <div className="text-sm font-medium text-gray-700">
                        {getGameDef(activeMatch.gameId)?.icon} {getGameDef(activeMatch.gameId)?.name}
                    </div>
                </div>
                <Card>
                    <CardContent className="p-5">
                        {renderMultiplayerGame(activeMatch, user._id, leaveMatch)}
                    </CardContent>
                </Card>
            </div>
        );
    }

    // ── Active single-player game ────────────────────────────────────────
    if (activeGameId) {
        const game = getGameDef(activeGameId);
        return (
            <div className="container mx-auto px-4 py-6">
                <div className="flex items-center justify-between mb-4">
                    <Button size="sm" variant="outline" onClick={() => setActiveGameId(null)}>
                        <ArrowLeft className="h-3.5 w-3.5 mr-1.5" />
                        Back to games
                    </Button>
                    <div className="text-sm font-medium text-gray-700">
                        {game?.icon} {game?.name}
                    </div>
                </div>
                <Card>
                    <CardContent className="p-5 flex justify-center">
                        {renderSinglePlayerGame(activeGameId, handleSinglePlayerScore)}
                    </CardContent>
                </Card>
            </div>
        );
    }

    // ── Hub page ─────────────────────────────────────────────────────────
    return (
        <div className="container mx-auto px-4 sm:px-6 py-4 max-w-7xl">
            {/* Hero */}
            <div className="games-hero p-5 md:p-6 mb-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-white/10 rounded-xl">
                        <Gamepad2 className="h-6 w-6 text-white" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold tracking-tight">Games</h1>
                        <p className="text-xs text-gray-300 mt-0.5">Quick brain breaks between calls — challenge a colleague or play solo</p>
                    </div>
                </div>
                {stats && (
                    <div className="flex items-center gap-3 text-xs">
                        <HeroStat label="Today" value={`${Math.round((stats.playMsToday || 0) / 60000)}m`} />
                        {stats.winStreak > 0 && <HeroStat label="🔥 Streak" value={stats.winStreak} />}
                        {stats.dailyLimitMinutes > 0 && (
                            <HeroStat label="Limit" value={`${stats.dailyLimitMinutes}m`} />
                        )}
                    </div>
                )}
            </div>

            {dailyLimitReached && (
                <div className="mb-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    You've reached your daily play limit. Back to leads! 💪
                </div>
            )}

            <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-5">
                {/* Game grid */}
                <div className="space-y-5">
                    <Section title="Single Player" subtitle="Play solo at your own pace">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {SINGLE_GAMES.map((g) => (
                                <GameCard key={g.id} game={g} onClick={() => setActiveGameId(g.id)} disabled={dailyLimitReached} />
                            ))}
                        </div>
                    </Section>

                    <Section
                        title="Multiplayer"
                        subtitle={canMultiplayer ? "Challenge an online colleague" : "Multiplayer disabled by admin"}
                    >
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {MULTI_GAMES.map((g) => (
                                <GameCard key={g.id} game={g} disabled={!canMultiplayer || dailyLimitReached} hint="Pick an agent →" />
                            ))}
                        </div>
                    </Section>
                </div>

                {/* Right sidebar */}
                <div className="space-y-4">
                    {/* Online players */}
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-1.5">
                                    <Users className="h-3.5 w-3.5 text-gray-500" />
                                    Agents Online
                                </h3>
                                <span className="text-[10px] text-gray-400 tabular-nums">{online.length}</span>
                            </div>
                            {online.length === 0 ? (
                                <p className="text-xs text-gray-400 text-center py-4">Nobody else here yet</p>
                            ) : (
                                <ul className="space-y-1">
                                    {online.map((p) => {
                                        const isMe = String(p.userId) === String(user._id);
                                        if (isMe) return null;
                                        const inMatch = p.status === "playing";
                                        const canChallenge = canMultiplayer && p.multiplayerAllowed && !inMatch && !pendingChallengeTo;
                                        return (
                                            <li key={p.userId} className="flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-gray-50">
                                                <span className={inMatch ? "h-2 w-2 rounded-full bg-amber-500" : "crm-online-dot"} />
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-xs font-medium text-gray-900 truncate">{p.firstName} {p.lastName}</div>
                                                    <div className="text-[10px] text-gray-500 truncate">
                                                        {inMatch ? "in a match" : (p.positionName || p.role)}
                                                    </div>
                                                </div>
                                                {canChallenge && (
                                                    <button
                                                        onClick={() => setShowChallengeFor(p.userId)}
                                                        className="px-2 py-1 text-[10px] font-semibold text-pink-700 bg-pink-50 hover:bg-pink-100 rounded transition-colors flex items-center gap-1"
                                                        title="Challenge"
                                                    >
                                                        <Swords className="h-3 w-3" />
                                                        Challenge
                                                    </button>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </CardContent>
                    </Card>

                    {/* Leaderboard */}
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-2 mb-3">
                                <Trophy className="h-3.5 w-3.5 text-amber-500" />
                                <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Leaderboard</h3>
                            </div>

                            <div className="flex gap-1.5 mb-2">
                                {["daily", "weekly", "alltime"].map((p) => (
                                    <button
                                        key={p}
                                        onClick={() => setLeaderboardPeriod(p)}
                                        className={`flex-1 text-[10px] font-semibold uppercase tracking-wide py-1 rounded ${
                                            leaderboardPeriod === p ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                        }`}
                                    >
                                        {p === "alltime" ? "All time" : p}
                                    </button>
                                ))}
                            </div>

                            <select
                                value={leaderboardGame}
                                onChange={(e) => setLeaderboardGame(e.target.value)}
                                className="w-full mb-2 text-xs px-2 py-1 border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">All games</option>
                                {GAME_CATALOG.map((g) => (
                                    <option key={g.id} value={g.id}>{g.icon} {g.name}</option>
                                ))}
                            </select>

                            {leaderboard.length === 0 ? (
                                <p className="text-xs text-gray-400 text-center py-4">No scores yet</p>
                            ) : (
                                <ol className="space-y-1">
                                    {leaderboard.map((row, i) => (
                                        <li key={i} className="flex items-center gap-2 py-1 px-1.5 text-xs">
                                            <span className={`h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
                                                i === 0 ? "bg-amber-100 text-amber-700" :
                                                i === 1 ? "bg-gray-100 text-gray-700" :
                                                i === 2 ? "bg-orange-100 text-orange-700" :
                                                "bg-gray-50 text-gray-500"
                                            }`}>
                                                {i + 1}
                                            </span>
                                            <span className="flex-1 min-w-0 truncate font-medium text-gray-900">
                                                {row.firstName} {row.lastName}
                                            </span>
                                            <span className="text-[10px] text-gray-400">
                                                {getGameDef(row.gameId)?.icon}
                                            </span>
                                            <span className="text-xs font-bold text-gray-900 tabular-nums">{row.bestScore}</span>
                                        </li>
                                    ))}
                                </ol>
                            )}
                        </CardContent>
                    </Card>

                    {/* My stats */}
                    {stats?.perGame?.length > 0 && (
                        <Card>
                            <CardContent className="p-4">
                                <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                                    <Clock className="h-3.5 w-3.5 text-gray-500" />
                                    Your Records
                                </h3>
                                <ul className="space-y-1.5">
                                    {stats.perGame.map((s) => {
                                        const g = getGameDef(s._id);
                                        return (
                                            <li key={s._id} className="flex items-center justify-between text-xs">
                                                <span className="text-gray-700">{g?.icon} {g?.name || s._id}</span>
                                                <span className="font-bold tabular-nums text-gray-900">{s.bestScore}</span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </CardContent>
                        </Card>
                    )}
                </div>
            </div>

            {/* Challenge a-specific-agent picker */}
            {showChallengeFor && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowChallengeFor(null)}>
                    <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-sm font-semibold text-gray-900 mb-1">Pick a game</h3>
                        <p className="text-xs text-gray-500 mb-3">
                            Challenge {online.find((p) => p.userId === showChallengeFor)?.firstName || "agent"}
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                            {MULTI_GAMES.map((g) => (
                                <button
                                    key={g.id}
                                    onClick={() => challenge(showChallengeFor, g.id)}
                                    className="p-3 border border-gray-200 rounded-lg hover:border-pink-400 hover:bg-pink-50 transition-colors text-left"
                                >
                                    <div className="text-xl mb-1">{g.icon}</div>
                                    <div className="text-sm font-semibold text-gray-900">{g.name}</div>
                                    <div className="text-[10px] text-gray-500">{g.time}</div>
                                </button>
                            ))}
                        </div>
                        <Button variant="outline" size="sm" onClick={() => setShowChallengeFor(null)} className="w-full mt-3">Cancel</Button>
                    </div>
                </div>
            )}

            {/* Pending challenge spinner */}
            {pendingChallengeTo && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-white rounded-full shadow-2xl border border-gray-200 px-4 py-2 flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin text-pink-500" />
                    <span className="text-sm text-gray-700">Waiting for response...</span>
                    <button onClick={() => setPendingChallengeTo(null)} className="ml-1 p-0.5 rounded hover:bg-gray-100">
                        <X className="h-3.5 w-3.5 text-gray-400" />
                    </button>
                </div>
            )}
        </div>
    );
}

// ── Sub-components ───────────────────────────────────────────────────────
function Section({ title, subtitle, children }) {
    return (
        <div>
            <div className="mb-2.5">
                <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
                <p className="text-xs text-gray-500">{subtitle}</p>
            </div>
            {children}
        </div>
    );
}

function GameCard({ game, onClick, disabled, hint }) {
    return (
        <div
            onClick={disabled ? undefined : onClick}
            className={`games-card p-3 flex items-center gap-3 ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
        >
            <div className={`games-card-icon bg-gradient-to-br ${game.color}`}>
                {game.icon}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-gray-900 truncate">{game.name}</h3>
                    {game.mode === "multi" && (
                        <Badge variant="outline" className="text-[9px] h-4 px-1 bg-pink-50 text-pink-700 border-pink-200">PVP</Badge>
                    )}
                </div>
                <p className="text-xs text-gray-500 truncate">{game.description}</p>
                <div className="flex items-center gap-2 mt-0.5 text-[10px] text-gray-400">
                    <Clock className="h-2.5 w-2.5" />
                    {game.time}
                    {hint && <span className="ml-auto text-pink-600 font-medium">{hint}</span>}
                </div>
            </div>
        </div>
    );
}

function HeroStat({ label, value }) {
    return (
        <div className="text-center px-3 py-1.5 bg-white/10 rounded-md">
            <div className="text-[9px] uppercase tracking-wide opacity-70">{label}</div>
            <div className="text-sm font-bold tabular-nums">{value}</div>
        </div>
    );
}

// ── Game routers ─────────────────────────────────────────────────────────
function renderSinglePlayerGame(gameId, onScoreSubmit) {
    switch (gameId) {
        case "2048":     return <Game2048 onScoreSubmit={onScoreSubmit} />;
        case "reaction": return <GameReaction onScoreSubmit={onScoreSubmit} />;
        case "memory":   return <GameMemory onScoreSubmit={onScoreSubmit} />;
        case "snake":    return <GameSnake onScoreSubmit={onScoreSubmit} />;
        default:         return <p>Unknown game</p>;
    }
}

function renderMultiplayerGame(match, currentUserId, onLeave) {
    const onMove = (payload) => emitSocket("game:move", { matchId: match.id, payload });
    const onAnswer = ({ questionIndex, answerIndex, timeMs }) =>
        emitSocket("game:quiz_answer", { matchId: match.id, questionIndex, answerIndex, timeMs });
    const onProgress = ({ progress, correct }) =>
        emitSocket("game:typing_progress", { matchId: match.id, progress, correct });

    switch (match.gameId) {
        case "tictactoe":
            return <GameTicTacToe match={match} currentUserId={currentUserId} onMove={onMove} onLeave={onLeave} />;
        case "rps":
            return <GameRPS match={match} currentUserId={currentUserId} onMove={onMove} onLeave={onLeave} />;
        case "quiz":
            return <GameQuiz match={match} currentUserId={currentUserId} onAnswer={onAnswer} onLeave={onLeave} />;
        case "typing":
            return <GameTyping match={match} currentUserId={currentUserId} onProgress={onProgress} onLeave={onLeave} />;
        default:
            return <p>Unknown game</p>;
    }
}
